import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard } from "@/components/kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Printer, Pencil, Trash2, Sparkles, Wallet, Users, Copy, Eye, EyeOff } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { openPayslipPrint, openPayslipsBatchPrint } from "@/lib/payslipTemplate";

const EARNINGS = [
  ["basic", "Basic Salary"], ["hra", "House Rent Allowance"], ["conveyance", "Conveyance Allowance"],
  ["special_allowance", "Special Allowance"], ["other_earnings", "Other Earnings"],
];
const DEDUCTIONS = [
  ["pf", "Provident Fund (PF)"], ["professional_tax", "Professional Tax"],
  ["advance", "Advance / Loan"], ["other_deductions", "Other Deductions"],
];
const DAY_FIELDS = ["working_days", "paid_days", "lop_days", "present_days", "half_days"];

const SALARY_COLS = [["basic", "Basic"], ["hra", "HRA"], ["conveyance", "Conv."], ["special_allowance", "Special"], ["other_earnings", "Other"], ["pf", "PF"], ["professional_tax", "P.Tax"]];
const EARN_KEYS = ["basic", "hra", "conveyance", "special_allowance", "other_earnings"];

const emptyForm = {
  employee_id: "", month: new Date().toISOString().slice(0, 7),
  working_days: 26, paid_days: 0, lop_days: 0, present_days: 0, half_days: 0,
  basic: 0, hra: 0, conveyance: 0, special_allowance: 0, other_earnings: 0,
  pf: 0, professional_tax: 0, advance: 0, other_deductions: 0,
  paid_on: "", payment_mode: "Bank Transfer", remarks: "",
};

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Payslips() {
  const { user } = useAuth();
  const isAdmin = ["founder", "admin"].includes(user?.role);
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMonth, setBulkMonth] = useState(new Date().toISOString().slice(0, 7));
  const [bulkWorkingDays, setBulkWorkingDays] = useState(26);
  const [bulkSelected, setBulkSelected] = useState([]);
  const [salaryOpen, setSalaryOpen] = useState(false);
  const [salaryDraft, setSalaryDraft] = useState({});
  const [reveal, setReveal] = useState(false);
  const showAmt = (v) => (reveal ? inr(v) : "₹ ••••••");

  const { data: payslips = [], isLoading } = useQuery({
    queryKey: ["payslips"],
    queryFn: async () => (await api.get("/payslips")).data,
  });
  const { data: employees = [] } = useQuery({
    queryKey: ["employees"],
    queryFn: async () => (await api.get("/employees")).data,
    enabled: isAdmin,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const months = useMemo(() => {
    const m = {};
    payslips.forEach((p) => { m[p.month] = p.month_label || p.month; });
    return Object.entries(m).sort((a, b) => b[0].localeCompare(a[0]));
  }, [payslips]);
  const [printMonth, setPrintMonth] = useState("");
  useEffect(() => { if (!printMonth && months.length) setPrintMonth(months[0][0]); }, [months]); // eslint-disable-line

  const handlePrintAll = () => {
    const list = payslips.filter((p) => p.month === printMonth);
    if (!list.length) { toast.error("No payslips for this month."); return; }
    const label = months.find(([m]) => m === printMonth)?.[1] || printMonth;
    openPayslipsBatchPrint(list, label);
  };

  const gross = useMemo(() => EARNINGS.reduce((s, [k]) => s + Number(form[k] || 0), 0), [form]);
  const totalDed = useMemo(() => DEDUCTIONS.reduce((s, [k]) => s + Number(form[k] || 0), 0), [form]);
  const net = gross - totalDed;

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (p) => { setEditingId(p.payslip_id); setForm({ ...emptyForm, ...p }); setOpen(true); };

  const pickEmployee = async (v) => {
    const emp = employees.find((e) => e.employee_id === v);
    const sal = emp?.salary_structure || {};
    setForm((f) => ({
      ...f, employee_id: v,
      basic: +sal.basic || 0, hra: +sal.hra || 0, conveyance: +sal.conveyance || 0,
      special_allowance: +sal.special_allowance || 0, other_earnings: +sal.other_earnings || 0,
      pf: +sal.pf || 0, professional_tax: +sal.professional_tax || 0,
    }));
    try {
      const { emi } = (await api.get(`/loans/emi/${v}`)).data;
      if (emi > 0) {
        set("advance", emi);
        toast.info(`Active loan found — EMI ₹${Number(emi).toLocaleString("en-IN")} pre-filled into Advance/Loan deduction`);
      }
    } catch { /* ignore */ }
  };

  const bulkGen = useMutation({
    mutationFn: () => api.post("/payslips/bulk", { month: bulkMonth, employee_ids: bulkSelected, working_days: Number(bulkWorkingDays) || 26 }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["payslips"] });
      const { created, skipped } = res.data;
      toast.success(`${created.length} payslip(s) generated${skipped.length ? `, ${skipped.length} skipped (already exist)` : ""}`);
      setBulkOpen(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Bulk generation failed"),
  });

  const openSalary = () => {
    const d = {};
    employees.forEach((e) => {
      const s = e.salary_structure || {};
      d[e.employee_id] = {
        basic: +s.basic || 0, hra: +s.hra || 0, conveyance: +s.conveyance || 0,
        special_allowance: +s.special_allowance || 0, other_earnings: +s.other_earnings || 0,
        pf: +s.pf || 0, professional_tax: +s.professional_tax || 0,
      };
    });
    setSalaryDraft(d);
    setSalaryOpen(true);
  };

  const saveSalaries = useMutation({
    mutationFn: () => api.put("/employees/salary-structures", { items: salaryDraft }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["employees"] });
      toast.success(`Salary structure saved for ${res.data.updated} employee(s)`);
      setSalaryOpen(false);
    },
    onError: () => toast.error("Save failed"),
  });

  const grossOf = (eid) => EARN_KEYS.reduce((s, k) => s + (+((salaryDraft[eid] || {})[k]) || 0), 0);
  const copyRowToAll = (eid) => {
    const src = { ...(salaryDraft[eid] || {}) };
    setSalaryDraft((prev) => {
      const next = {};
      Object.keys(prev).forEach((k) => { next[k] = { ...src }; });
      return next;
    });
    toast.success("Copied this salary structure to all employees");
  };

  const autoFill = async () => {
    if (!form.employee_id) { toast.error("Select an employee first"); return; }
    try {
      const s = (await api.get("/payslips/attendance-summary", { params: { employee_id: form.employee_id, month: form.month } })).data;
      const emp = employees.find((e) => e.employee_id === form.employee_id);
      const sal = emp?.salary_structure || {};
      const wd = s.working_days || 0;
      const factor = wd ? s.paid_days / wd : 0;
      const pr = (k) => Math.round((+sal[k] || 0) * factor);
      setForm((f) => ({
        ...f, working_days: wd, present_days: s.present_days, half_days: s.half_days,
        paid_days: s.paid_days, lop_days: Math.max(0, wd - s.paid_days),
        basic: pr("basic"), hra: pr("hra"), conveyance: pr("conveyance"),
        special_allowance: pr("special_allowance"), other_earnings: pr("other_earnings"),
      }));
      toast.success(`Period ${s.period_start} → ${s.period_end}: ${s.paid_days}/${wd} working days — salary prorated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load attendance for this month");
    }
  };

  const save = useMutation({
    mutationFn: async () => {
      if (!form.employee_id) throw new Error("Select an employee");
      if (!form.month) throw new Error("Select a month");
      const numeric = [...EARNINGS, ...DEDUCTIONS].map(([k]) => k).concat(DAY_FIELDS);
      const payload = { ...form };
      numeric.forEach((k) => { payload[k] = Number(form[k] || 0); });
      if (editingId) return api.put(`/payslips/${editingId}`, payload);
      return api.post("/payslips", payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payslips"] });
      setOpen(false);
      toast.success(editingId ? "Payslip updated" : "Payslip created");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e?.message || "Save failed"),
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/payslips/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["payslips"] }); toast.success("Payslip deleted"); },
    onError: () => toast.error("Delete failed"),
  });

  if (isLoading)
    return (
      <div>
        <PageHeader title="Payslips" subtitle="Monthly salary slips" />
        <Skeleton className="h-64 rounded-lg" />
      </div>
    );

  return (
    <div data-testid="payslips-page">
      <PageHeader
        title="Payslips"
        subtitle={isAdmin ? "Create & manage monthly salary slips for employees" : "Your monthly salary slips"}
      >
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setReveal((r) => !r)} data-testid="toggle-salary-mask" className="rounded-lg">
            {reveal ? <EyeOff className="mr-2 h-4 w-4" /> : <Eye className="mr-2 h-4 w-4" />}
            {reveal ? "Hide amounts" : "Reveal amounts"}
          </Button>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            {months.length > 0 && (
              <>
                <Select value={printMonth} onValueChange={setPrintMonth}>
                  <SelectTrigger className="w-40" data-testid="print-all-month"><SelectValue placeholder="Month" /></SelectTrigger>
                  <SelectContent>
                    {months.map(([m, label]) => <SelectItem key={m} value={m}>{label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={handlePrintAll} data-testid="payslip-print-all-btn" className="rounded-lg border-navy text-navy hover:bg-navy/5">
                  <Printer className="mr-2 h-4 w-4" /> Print All
                </Button>
              </>
            )}
            <Button variant="outline" onClick={openSalary} data-testid="salary-setup-btn" className="rounded-lg border-gold text-gold-dark hover:bg-gold/10">
              <Sparkles className="mr-2 h-4 w-4" /> Salary Setup
            </Button>
            <Button variant="outline" onClick={() => { setBulkSelected(employees.map((e) => e.employee_id)); setBulkOpen(true); }} data-testid="payslip-bulk-btn" className="rounded-lg border-navy text-navy hover:bg-navy/5">
              <Users className="mr-2 h-4 w-4" /> Bulk Generate
            </Button>
            <Button onClick={openCreate} data-testid="payslip-create-btn" className="bg-navy hover:bg-navy-900 text-white rounded-lg">
              <Plus className="mr-2 h-4 w-4" /> New Payslip
            </Button>
          </div>
        )}
      </PageHeader>

      <SectionCard title={isAdmin ? "All Payslips" : "My Payslips"} testId="payslips-list">
        {payslips.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 text-center" data-testid="payslips-empty">
            <div className="rounded-full bg-gold/15 p-4 mb-3"><Wallet className="h-8 w-8 text-gold-dark" /></div>
            <p className="text-sm font-medium text-slate-500">No payslips yet</p>
            <p className="mt-1 text-xs text-slate-400">
              {isAdmin ? "Create a payslip to get started." : "Your payslips will appear here once issued by HR."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  {isAdmin && <TableHead>Employee</TableHead>}
                  <TableHead className="text-right">Paid Days</TableHead>
                  <TableHead className="text-right">Gross</TableHead>
                  <TableHead className="text-right">Deductions</TableHead>
                  <TableHead className="text-right">Net Pay</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payslips.map((p) => (
                  <TableRow key={p.payslip_id} data-testid={`payslip-row-${p.payslip_id}`}>
                    <TableCell className="font-medium text-navy">{p.month_label || p.month}</TableCell>
                    {isAdmin && <TableCell>{p.employee_name} <span className="text-xs text-slate-400">({p.employee_id})</span></TableCell>}
                    <TableCell className="text-right">{p.paid_days}</TableCell>
                    <TableCell className="text-right">{showAmt(p.gross_earnings)}</TableCell>
                    <TableCell className="text-right text-red-600">{showAmt(p.total_deductions)}</TableCell>
                    <TableCell className="text-right font-semibold text-emerald-700">{showAmt(p.net_pay)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openPayslipPrint(p)} data-testid={`payslip-print-${p.payslip_id}`} title="Print / Download">
                          <Printer className="h-4 w-4 text-navy" />
                        </Button>
                        {isAdmin && (
                          <>
                            <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`payslip-edit-${p.payslip_id}`} title="Edit">
                              <Pencil className="h-4 w-4 text-slate-500" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="icon" variant="ghost" data-testid={`payslip-delete-${p.payslip_id}`} title="Delete">
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete this payslip?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Payslip for {p.employee_name} — {p.month_label || p.month}. This cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => del.mutate(p.payslip_id)} className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </SectionCard>

      {isAdmin && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-navy">{editingId ? "Edit Payslip" : "New Payslip"}</DialogTitle>
            </DialogHeader>

            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-1">
                  <Label>Employee</Label>
                  <Select value={form.employee_id} onValueChange={pickEmployee} disabled={!!editingId}>
                    <SelectTrigger data-testid="payslip-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                    <SelectContent>
                      {employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name} ({e.employee_id})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Month</Label>
                  <Input type="month" data-testid="payslip-month-input" value={form.month} onChange={(e) => set("month", e.target.value)} disabled={!!editingId} />
                </div>
                <div className="flex items-end">
                  <Button type="button" variant="outline" onClick={autoFill} data-testid="payslip-autofill-btn" className="w-full rounded-lg border-gold text-gold-dark hover:bg-gold/10">
                    <Sparkles className="mr-2 h-4 w-4" /> Auto-fill Attendance
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {[["working_days", "Working Days"], ["present_days", "Present"], ["half_days", "Half Days"], ["paid_days", "Paid Days"], ["lop_days", "LOP Days"]].map(([k, l]) => (
                  <div key={k}>
                    <Label className="text-xs">{l}</Label>
                    <Input type="number" step="0.5" data-testid={`payslip-${k}`} value={form[k]} onChange={(e) => set(k, e.target.value)} />
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="rounded-lg border border-slate-200 p-4">
                  <p className="font-heading text-sm font-semibold text-navy mb-3">Earnings</p>
                  <div className="space-y-2">
                    {EARNINGS.map(([k, l]) => (
                      <div key={k} className="flex items-center gap-2">
                        <Label className="flex-1 text-xs text-slate-600">{l}</Label>
                        <Input type="number" className="w-32" data-testid={`payslip-${k}`} value={form[k]} onChange={(e) => set(k, e.target.value)} />
                      </div>
                    ))}
                    <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                      <span className="flex-1 text-sm font-semibold text-navy">Gross Earnings</span>
                      <span className="w-32 text-right text-sm font-bold text-navy" data-testid="payslip-gross">{inr(gross)}</span>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-slate-200 p-4">
                  <p className="font-heading text-sm font-semibold text-navy mb-3">Deductions</p>
                  <div className="space-y-2">
                    {DEDUCTIONS.map(([k, l]) => (
                      <div key={k} className="flex items-center gap-2">
                        <Label className="flex-1 text-xs text-slate-600">{l}</Label>
                        <Input type="number" className="w-32" data-testid={`payslip-${k}`} value={form[k]} onChange={(e) => set(k, e.target.value)} />
                      </div>
                    ))}
                    <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                      <span className="flex-1 text-sm font-semibold text-navy">Total Deductions</span>
                      <span className="w-32 text-right text-sm font-bold text-red-600" data-testid="payslip-deductions">{inr(totalDed)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-lg bg-gold/15 px-4 py-3 flex items-center justify-between">
                <span className="font-heading text-base font-bold text-navy">Net Pay</span>
                <span className="font-heading text-xl font-extrabold text-navy" data-testid="payslip-net">{inr(net)}</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label>Paid On</Label>
                  <Input type="date" data-testid="payslip-paid-on" value={form.paid_on} onChange={(e) => set("paid_on", e.target.value)} />
                </div>
                <div>
                  <Label>Payment Mode</Label>
                  <Input data-testid="payslip-mode" value={form.payment_mode} onChange={(e) => set("payment_mode", e.target.value)} />
                </div>
                <div>
                  <Label>Remarks</Label>
                  <Input data-testid="payslip-remarks" value={form.remarks} onChange={(e) => set("remarks", e.target.value)} />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="payslip-save-btn" className="bg-navy text-white">
                {editingId ? "Save Changes" : "Create Payslip"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {isAdmin && (
        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading text-navy">Bulk Generate Payslips</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Month</Label><Input type="month" data-testid="bulk-month-input" value={bulkMonth} onChange={(e) => setBulkMonth(e.target.value)} /></div>
                <div><Label>Working Days</Label><Input type="number" data-testid="bulk-workingdays-input" value={bulkWorkingDays} onChange={(e) => setBulkWorkingDays(e.target.value)} /></div>
              </div>
              <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-500">
                Period runs 4th→4th of the month, <b>excluding Saturdays &amp; Sundays</b>. Working days and days-worked are auto-calculated from attendance, and each employee's salary is <b>auto-prorated</b> for the days they actually worked. Active loan EMI is added to deductions. Employees who already have a payslip for this month are skipped.
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Employees ({bulkSelected.length} selected)</Label>
                  <button type="button" className="text-xs text-navy underline" data-testid="bulk-selectall"
                    onClick={() => setBulkSelected(bulkSelected.length === employees.length ? [] : employees.map((e) => e.employee_id))}>
                    {bulkSelected.length === employees.length ? "Clear all" : "Select all"}
                  </button>
                </div>
                <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200 divide-y">
                  {employees.map((e) => (
                    <label key={e.employee_id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50" data-testid={`bulk-emp-${e.employee_id}`}>
                      <Checkbox checked={bulkSelected.includes(e.employee_id)}
                        onCheckedChange={() => setBulkSelected((s) => s.includes(e.employee_id) ? s.filter((x) => x !== e.employee_id) : [...s, e.employee_id])} />
                      <span className="text-sm">{e.name} <span className="text-xs text-slate-400">({e.employee_id})</span></span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
              <Button onClick={() => bulkGen.mutate()} disabled={bulkGen.isPending || !bulkSelected.length || !bulkMonth} data-testid="bulk-generate-btn" className="bg-navy text-white">
                Generate{bulkSelected.length ? ` (${bulkSelected.length})` : ""}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {isAdmin && (
        <Dialog open={salaryOpen} onOpenChange={setSalaryOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading text-navy">Salary Structure — All Employees</DialogTitle></DialogHeader>
            <p className="text-xs text-slate-500">Set each employee's monthly salary once. Used to auto-fill payslips. The Advance/Loan EMI is added automatically at generation.</p>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    {SALARY_COLS.map(([k, l]) => <TableHead key={k} className="text-right">{l}</TableHead>)}
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">Copy</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employees.map((e) => (
                    <TableRow key={e.employee_id} data-testid={`salary-row-${e.employee_id}`}>
                      <TableCell className="whitespace-nowrap text-sm">{e.name}<div className="text-xs text-slate-400">{e.employee_id}</div></TableCell>
                      {SALARY_COLS.map(([k]) => (
                        <TableCell key={k}>
                          <Input type="number" className="w-24 h-8 text-right" data-testid={`salary-${e.employee_id}-${k}`}
                            value={salaryDraft[e.employee_id]?.[k] ?? 0}
                            onChange={(ev) => setSalaryDraft((prev) => ({ ...prev, [e.employee_id]: { ...prev[e.employee_id], [k]: ev.target.value } }))} />
                        </TableCell>
                      ))}
                      <TableCell className="text-right font-semibold text-navy whitespace-nowrap" data-testid={`salary-gross-${e.employee_id}`}>{showAmt(grossOf(e.employee_id))}</TableCell>
                      <TableCell className="text-right">
                        <Button size="icon" variant="ghost" title="Copy this row to all employees" data-testid={`salary-copy-${e.employee_id}`} onClick={() => copyRowToAll(e.employee_id)}>
                          <Copy className="h-4 w-4 text-gold-dark" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSalaryOpen(false)}>Cancel</Button>
              <Button onClick={() => saveSalaries.mutate()} disabled={saveSalaries.isPending} data-testid="salary-save-all-btn" className="bg-navy text-white">Save All</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
